import { isReference } from "@apollo/client/cache";
import { useMutation } from "@apollo/client/react";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  Typography,
  outlinedInputClasses,
  styled,
  useTheme,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { format } from "date-fns";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EditableTextField } from "../../../../../components/EditableTextField/EditableTextField";
import { IfPermission, hasPermission } from "../../../../../components/Permissions";
import { EditableField } from "../../../../../components/common/EditableFields";
import { StatefulButton } from "../../../../../components/ui/muiButtons";
import Permissions from "../../../../../constants/permissions";
import { timezoneList } from "../../../../../constants/timezoneList";
import { UserContext } from "../../../../../context";
import { User } from "../../../../../context/UserContext";
import {
  AccountDetail,
  SaveAccountV2Mutation,
  SaveAccountV2MutationVariables,
  SaveAccountV2Document,
} from "../../../../../graphql/types";

import { clockClasses } from "@mui/x-date-pickers";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { BaseAdminContainer } from "../../../../../components/ui/common";
import { validateEmail } from "../../../../../lib/Util";

const GridContainer = styled("div")({
  display: "grid",
  gridTemplateColumns: "250px 250px",
  gap: "50px",
  paddingLeft: "8px",
});

const errorToast = (errorMessage: string) => {
  toast.error(errorMessage);
};

const BasicTimePicker = ({
  onClose,
  loading,
  user,
  defaultValues,
  textfieldId,
}: {
  onClose: (time: Date, timezone: string) => void;
  loading: boolean;
  user: User;
  defaultValues: DefaultValuesTimePicker;
  textfieldId: string;
}) => {
  const [open, setOpen] = useState(false);
  const { control, getValues, reset } = useForm<DefaultValuesTimePicker>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: defaultValues,
  });
  const theme = useTheme();

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const validateTimePicker = (value: string, formValues: DefaultValuesTimePicker) => {
    // isValid could be false despite no errors https://github.com/react-hook-form/react-hook-form/issues/2755
    const time = formValues.timeName;
    const timezone = formValues.timezoneName;
    if (time && !timezone) {
      return "Invalid timezone";
    } else if (!time && timezone) {
      return "Invalid time";
    } else if (!time && !timezone) {
      return "Invalid time and timezone";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Controller
        name="timeName"
        control={control}
        render={({ field: { onChange, value } }) => (
          <DesktopTimePicker
            views={["hours"]}
            value={value || null}
            onChange={(value) => onChange(value)}
            ampmInClock
            onOpen={() => setOpen(true)}
            open={open}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            onClose={() => {
              const timeName = getValues("timeName");
              const timezoneName = getValues("timezoneName");
              if (timeName && timezoneName) {
                onClose(timeName, timezoneName);
              } else {
                const errorMsg = validateTimePicker("a", getValues()) || "";
                errorToast(errorMsg);
              }
              setOpen(false);
            }}
            disabled={!hasPermission(Permissions.ManageAccountSettings, user)}
            closeOnSelect={false}
            slots={{
              inputAdornment: (params) => (
                <Stack
                  ref={params.ref}
                  sx={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", width: "100%" }}
                >
                  <TypographyTimezone data-testid={"timezone-label"}>{getValues("timezoneName")}</TypographyTimezone>
                  <IconButton onClick={() => setOpen(true)} aria-label="Open time picker">
                    <ClockIcon />
                  </IconButton>
                </Stack>
              ),
              leftArrowIcon: () => null,
              rightArrowIcon: () => null,
              toolbar: () => {
                if (loading) {
                  return (
                    <Stack
                      sx={{
                        flexDirection: "row",
                        width: "100%",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: 2,
                        minHeight: "50px",
                      }}
                    >
                      <CircularProgress size={20} />
                    </Stack>
                  );
                }
                return (
                  <Stack sx={{ alignItems: "center" }} data-testid="time-picker-something">
                    <Controller
                      control={control}
                      name="timezoneName"
                      render={({ field: { onChange, value } }) => {
                        return (
                          <Stack sx={{ mt: 2, alignItems: "center" }}>
                            <AutocompleteStyled
                              disablePortal
                              options={timezoneList}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  id="timezoneName"
                                  label="Timezone"
                                  sx={{
                                    "& .MuiInputLabel-root": {
                                      flexDirection: "row",
                                      justifyContent: "center",
                                    },
                                  }}
                                />
                              )}
                              onChange={(event, newValue: string | null) => {
                                onChange(newValue);
                              }}
                              value={value}
                              slotProps={{ listbox: { style: { maxHeight: "100px" } } }}
                            />
                          </Stack>
                        );
                      }}
                    />
                  </Stack>
                );
              },
            }}
            slotProps={{
              toolbar: {
                hidden: false,
              },
              desktopPaper: {
                sx: {
                  "> div ": {
                    display: "flex",
                    flexDirection: "column",
                  },
                  [`& .${clockClasses.root} .${clockClasses.selected}`]: {
                    color: theme.palette.text10.main,
                  },
                },
              },
              textField: {
                id: textfieldId,
                sx: {
                  [`& .${outlinedInputClasses.input}`]: {
                    width: getValues("timezoneName") ? "80px" : "260px",
                  },
                },
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

const AutocompleteStyled = styled(Autocomplete)({
  width: "250px",
}) as typeof Autocomplete;

const TypographyTimezone = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "10px",
}));

const ListContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "column",
  gridTemplateRows: "repeat(5, auto)",
  overflowX: "auto",
  overflowY: "hidden",
  minHeight: "300px",
  paddingBottom: theme.spacing(2),
}));

type DefaultValuesTimePicker = {
  timeName: Date | null;
  timezoneName: string | null;
};

type DefaultValuesRecipientsFormType = {
  recipients?: string[] | null;
  email: string;
};

type FormType = "summaryTime" | "defaultDeadline" | "recipients";

interface IDailySummary {
  dataQuery: AccountDetail;
}

const getDefaultValues = (data: AccountDetail, form?: FormType) => {
  const incomingData = data;
  const [timeDefaultDeadline, timezoneDefaultDeadline] = incomingData?.defaultDeadline
    ? incomingData.defaultDeadline.split(" ")
    : [null, null];
  const [timeSummaryTime, timezoneSummaryTime] = incomingData?.dailySummaryTime
    ? incomingData.dailySummaryTime.split(" ")
    : [null];
  const [hourDD, minuteDD] = timeDefaultDeadline ? timeDefaultDeadline.split(":") : [null, null];
  const defaultDeadline = hourDD && minuteDD ? new Date(1, 1, 2022, Number(hourDD), Number(minuteDD)) : null;
  const recipients = incomingData?.dailySummaryRecipients || [];
  const [hourST, minuteST] = timeSummaryTime ? timeSummaryTime.split(":") : [null, null];
  const summaryTime = hourST && minuteST ? new Date(1, 1, 2022, Number(hourST), Number(minuteST)) : null;
  if (form === "summaryTime") {
    return { summaryTime, timezoneSummaryTime };
  } else if (form === "defaultDeadline") {
    return { defaultDeadline, timezoneDefaultDeadline };
  } else if (form === "recipients") {
    return { recipients, email: "" };
  }
  return {
    defaultDeadline,
    recipients,
    summaryTime,
    timezoneDefaultDeadline,
    timezoneSummaryTime,
    email: "",
  };
};

const DownloadSummary = ({ account }: { account: AccountDetail }) => {
  const { control, setError, resetField, getValues } = useForm<{
    contractedRowCount?: number;
    showDownloadSummary?: boolean;
  }>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      contractedRowCount: account.contractedRowCount || undefined,
      showDownloadSummary: account.showDownloadSummary || false,
    },
  });
  const [isEditing, setIsEditing] = useState({
    contractedRowCount: false,
  });
  const [saveAccount, saveAccountState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {}
  );
  const contractedRowCountRef = useRef<HTMLInputElement>(null);
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    const newValue = parseInt(String(getValues("contractedRowCount")));

    switch (e.key) {
      case "Enter":
        if (saveAccountState.loading || newValue === account.contractedRowCount) return;
        e.preventDefault();
        if (!newValue && newValue !== 0) {
          setError("contractedRowCount", { message: "Required field" });
          return;
        }
        saveAccount({
          variables: {
            urlName: account.urlName,
            contractedRowCount: newValue,
          },
          update: (cache, { data }) => {
            if (data?.saveAccountV2.__typename !== "ResultOk") return;
            cache.modify({
              id: cache.identify({
                __typename: "Query",
                urlName: account.urlName,
              }),
              fields: {
                account(oldData) {
                  // A normalised field arrives as a Reference, which cannot be merged into.
                  if (isReference(oldData)) {
                    return oldData;
                  }
                  return {
                    ...oldData,
                    contractedRowCount: newValue,
                  };
                },
              },
            });
          },

          onCompleted: () => {
            toast.success("Contracted row count saved successfully");
            setIsEditing({ ...isEditing, contractedRowCount: false });
          },
          onError: () => {
            toast.error("Error when saving contracted row count");
          },
        });

        break;
      case "Escape":
        resetField("contractedRowCount", {
          defaultValue: account.contractedRowCount || undefined,
        });
        setIsEditing({ ...isEditing, contractedRowCount: false });
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (contractedRowCountRef.current && isEditing.contractedRowCount) {
      contractedRowCountRef.current?.focus();
    }
  }, [isEditing.contractedRowCount]);

  const updateDownloadSummary = (showDownloadSummary: boolean) => {
    saveAccount({
      variables: {
        urlName: account.urlName,
        showDownloadSummary: showDownloadSummary,
      },
      update: (cache, { data }) => {
        if (data?.saveAccountV2.__typename !== "ResultOk") return;
        cache.modify({
          id: cache.identify({
            __typename: "Query",
            urlName: account.urlName,
          }),
          fields: {
            account(oldData) {
              return {
                ...oldData,
                showDownloadSummary: showDownloadSummary,
              };
            },
          },
        });
      },
      onCompleted: () => {
        toast.success(`Download summary is ${showDownloadSummary ? "on" : "off"}`);
      },
      onError: () => {
        toast.error("Error when updating download summary visibility");
      },
    });
  };

  return (
    <>
      <Stack sx={{ mb: 1 }}>
        <Typography variant="heading.four">Download Summary</Typography>
      </Stack>
      <Grid container>
        <GridContainer>
          <div>
            <InputLabel>
              <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>Contracted row count</Typography>
            </InputLabel>
            {isEditing.contractedRowCount ? (
              <EditableTextField
                name="contractedRowCount"
                control={control}
                rules={{ required: true }}
                onKeyDown={(e) => handleKeyDown(e)}
                inputRef={contractedRowCountRef}
                onBlur={() => {
                  resetField("contractedRowCount", {
                    defaultValue: account.contractedRowCount || undefined,
                  });
                  setIsEditing({ ...isEditing, contractedRowCount: false });
                }}
                isLoading={saveAccountState.loading}
                onFocus={(e) => e.currentTarget.select()}
                type="number"
              />
            ) : (
              <Stack direction="row" sx={{ alignItems: "center" }}>
                {" "}
                <Typography variant="heading.six">{account.contractedRowCount}</Typography>
                <IconButton
                  data-testid="edit-field-contractedRowCount"
                  onClick={() => {
                    setIsEditing({
                      ...isEditing,
                      contractedRowCount: true,
                    });
                  }}
                >
                  <PencilIcon />
                </IconButton>
              </Stack>
            )}
          </div>
          <div>
            <Controller
              name="showDownloadSummary"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Stack direction="column">
                  <InputLabel>
                    <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>Show download summary</Typography>
                  </InputLabel>
                  <Switch
                    checked={value}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      updateDownloadSummary(e.target.checked);
                    }}
                    data-testid="show-download-summary"
                  />
                </Stack>
              )}
            />
          </div>
        </GridContainer>
      </Grid>
    </>
  );
};

const SettingsTab = ({ dataQuery }: IDailySummary) => {
  const [showNewRecip, setShowNewRecip] = useState(false);
  const { url_name } = useParams();
  const { user } = useContext(UserContext);

  const { control, getValues, formState, resetField, reset } = useForm<DefaultValuesRecipientsFormType>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: getDefaultValues(dataQuery, "recipients"),
  });

  // https://github.com/react-hook-form/react-hook-form/issues/4055 issue solved in RHF 8
  //@ts-ignore
  const { fields } = useFieldArray({
    control: control,
    //@ts-ignore
    name: "recipients",
  });

  useEffect(() => {
    const { recipients } = getDefaultValues(dataQuery, "recipients");
    reset({
      recipients,
    });
  }, [dataQuery, reset]);

  const onCompleted = () => {
    toast.success("Daily Summary settings edited successfully");
  };
  const onError = () => {
    errorToast("Failed to edit Daily summary info");
  };

  const [saveDefaultDeadline, saveDefaultDeadlineState] = useMutation<
    SaveAccountV2Mutation,
    SaveAccountV2MutationVariables
  >(SaveAccountV2Document, {
    awaitRefetchQueries: false,
    onCompleted: onCompleted,
    onError: onError,
  });
  const [resetDefaultDeadline, resetDefaultDeadlineState] = useMutation<
    SaveAccountV2Mutation,
    SaveAccountV2MutationVariables
  >(SaveAccountV2Document, {
    awaitRefetchQueries: false,
    onCompleted: onCompleted,
    onError: onError,
  });
  const [saveSummaryTime, saveSummaryTimeState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {
      awaitRefetchQueries: false,
      onCompleted: onCompleted,
      onError: onError,
    }
  );
  const [resetSummaryTime, resetSummaryTimeState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {
      awaitRefetchQueries: false,
      onCompleted: onCompleted,
      onError: onError,
    }
  );
  const [saveRecipients, saveRecipientsState] = useMutation<SaveAccountV2Mutation, SaveAccountV2MutationVariables>(
    SaveAccountV2Document,
    {
      awaitRefetchQueries: false,
      onCompleted: onCompleted,
      onError: onError,
    }
  );

  return (
    <form>
      <BaseAdminContainer>
        <Grid container spacing={2}>
          <IfPermission permission={Permissions.ManageGlobalUsers}>
            <Grid container spacing={1}>
              <DownloadSummary account={dataQuery} />
            </Grid>
          </IfPermission>

          <Grid size={12} sx={{ mt: 1, mb: 1 }}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid>
            <Typography variant="heading.four">Daily Summary</Typography>
          </Grid>
          <Grid container size={12} spacing={2}>
            <Grid>
              <InputLabel htmlFor="timeNameDefaultDeadline">
                <Typography sx={{ fontSize: "14px", fontWeight: "600", mb: 1 }}>Default Deadline</Typography>
              </InputLabel>
              <Grid container sx={{ alignItems: "center" }} data-testid="default-deadline">
                <BasicTimePicker
                  textfieldId="timeNameDefaultDeadline"
                  onClose={(defaultDeadline, defaultDeadlineTimezone) => {
                    saveDefaultDeadline({
                      variables: {
                        urlName: url_name ?? "",
                        setDefaultDeadline: `${format(defaultDeadline, "hh:mm a")} ${defaultDeadlineTimezone}`,
                      },
                      update: (cache, { data }) => {
                        if (data?.saveAccountV2.__typename !== "ResultOk") return;
                        const newDefaultDeadline = `${format(defaultDeadline, "HH:mm")} ${defaultDeadlineTimezone}`;
                        cache.modify({
                          id: cache.identify({
                            __typename: "Query",
                            urlName: dataQuery.urlName,
                          }),
                          fields: {
                            account(oldData) {
                              return {
                                ...oldData,
                                defaultDeadline: newDefaultDeadline,
                              };
                            },
                          },
                        });
                      },
                    });
                  }}
                  loading={saveDefaultDeadlineState.loading}
                  user={user}
                  defaultValues={{
                    timeName: getDefaultValues(dataQuery, "defaultDeadline").defaultDeadline || null,
                    timezoneName: getDefaultValues(dataQuery, "defaultDeadline").timezoneDefaultDeadline || null,
                  }}
                />
                {dataQuery.defaultDeadline ? (
                  <IfPermission permission={Permissions.ManageAccountSettings}>
                    <StatefulButton
                      loading={resetDefaultDeadlineState.loading}
                      variant="text"
                      onClick={() => {
                        resetDefaultDeadline({
                          variables: {
                            urlName: dataQuery.urlName,
                            removeDefaultDeadline: true,
                          },
                          update: (cache, { data }) => {
                            if (data?.saveAccountV2.__typename !== "ResultOk") return;
                            cache.modify({
                              id: cache.identify({
                                __typename: "Query",
                                urlName: dataQuery.urlName,
                              }),
                              fields: {
                                account(oldData) {
                                  return {
                                    ...oldData,
                                    defaultDeadline: null,
                                  };
                                },
                              },
                            });
                          },
                        });
                      }}
                      aria-label="Reset default deadline"
                    >
                      Reset
                    </StatefulButton>
                  </IfPermission>
                ) : null}
              </Grid>
            </Grid>
            <Grid>
              <InputLabel htmlFor="timeNameSummaryTime">
                <Typography sx={{ fontSize: "14px", fontWeight: "600", mb: 1 }}>Summary Time</Typography>
              </InputLabel>
              <Grid container size={12} data-testid="summary-time" sx={{ alignItems: "center" }}>
                <BasicTimePicker
                  textfieldId="timeNameSummaryTime"
                  onClose={(summaryTime, timezoneSummaryTime) => {
                    saveSummaryTime({
                      variables: {
                        urlName: url_name ?? "",
                        setSummaryTime: `${format(summaryTime, "hh:mm a")} ${timezoneSummaryTime}`,
                      },
                      update: (cache, { data }) => {
                        if (data?.saveAccountV2.__typename !== "ResultOk") return;
                        const newSummaryTime = `${format(summaryTime, "HH:mm")} ${timezoneSummaryTime}`;
                        cache.modify({
                          id: cache.identify({
                            __typename: "Query",
                            urlName: dataQuery.urlName,
                          }),
                          fields: {
                            account(oldData) {
                              return {
                                ...oldData,
                                dailySummaryTime: newSummaryTime,
                              };
                            },
                          },
                        });
                      },
                    });
                  }}
                  loading={saveSummaryTimeState.loading}
                  user={user}
                  defaultValues={{
                    timeName: getDefaultValues(dataQuery, "summaryTime").summaryTime || null,
                    timezoneName: getDefaultValues(dataQuery, "summaryTime").timezoneSummaryTime || null,
                  }}
                />

                {dataQuery.dailySummaryTime ? (
                  <IfPermission permission={Permissions.ManageAccountSettings}>
                    <StatefulButton
                      variant="text"
                      loading={resetSummaryTimeState.loading}
                      onClick={() => {
                        resetSummaryTime({
                          variables: {
                            urlName: dataQuery.urlName,
                            removeSummaryTime: true,
                          },
                          update: (cache, { data }) => {
                            if (data?.saveAccountV2.__typename !== "ResultOk") return;
                            cache.modify({
                              id: cache.identify({
                                __typename: "Query",
                                urlName: dataQuery.urlName,
                              }),
                              fields: {
                                account(oldData) {
                                  return {
                                    ...oldData,
                                    dailySummaryTime: null,
                                  };
                                },
                              },
                            });
                          },
                        });
                      }}
                      aria-label="Reset summary time"
                    >
                      Reset
                    </StatefulButton>
                  </IfPermission>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} sx={{ mt: 1, mb: 1 }}>
            <Divider orientation="horizontal" />
          </Grid>
          <Grid container sx={{ justifyContent: "space-between", alignItems: "center", height: "60px" }}>
            <Grid>
              <Typography variant="heading.four">Recipients</Typography>
            </Grid>
            <Grid sx={{ marginLeft: "auto" }}>
              {showNewRecip ? null : (
                <IfPermission permission={Permissions.ManageAccountSettings}>
                  <Button
                    startIcon={<PlusIcon />}
                    onClick={() => setShowNewRecip(true)}
                    data-cy="add-recipient"
                    disabled={saveRecipientsState.loading}
                  >
                    Recipient
                  </Button>
                </IfPermission>
              )}
            </Grid>
          </Grid>

          <Grid size={12}>
            <ListContainer>
              {/* this should be checked with further info. ArrayItems returns any[] which is not allowed by ts */
              /* @ts-ignore */}
              {fields.map((field, index) => (
                <Stack key={field.id} sx={{ width: "400px", mt: 2, flexDirection: "row" }}>
                  <EditableField<DefaultValuesRecipientsFormType>
                    key={`recipients.${index}`}
                    control={control}
                    name={`recipients.${index}`}
                    rules={{ validate: validateEmail }}
                    helperText={(formState.errors?.recipients && formState.errors.recipients[index]?.message) || ""}
                    label=""
                    onClickDelete={() => {
                      const newRecipients = dataQuery.dailySummaryRecipients.filter(
                        (_item: unknown, i: number) => i !== index
                      );
                      const newVariables: SaveAccountV2MutationVariables = {
                        urlName: url_name ?? "",
                        setSummaryRecipients: newRecipients || [],
                      };
                      const sortedArray = [...newRecipients].sort();
                      saveRecipients({
                        variables: newVariables,
                        update: (cache, { data }) => {
                          if (data?.saveAccountV2.__typename !== "ResultOk") return;
                          cache.modify({
                            id: cache.identify({
                              __typename: "Query",
                              urlName: dataQuery.urlName,
                            }),
                            fields: {
                              account(oldData) {
                                return {
                                  ...oldData,
                                  dailySummaryRecipients: sortedArray,
                                };
                              },
                            },
                          });
                        },
                      });
                    }}
                    error={Boolean(formState.errors?.recipients && formState.errors.recipients[index]?.message)}
                    permission={Permissions.ManageAccountSettings}
                    dataCy="recipient-field"
                    onEscape={() => {
                      resetField(`recipients.${index}`);
                    }}
                    onEnter={() => {
                      const newSummaryRecipient = getValues(`recipients.${index}`);
                      // newRecipientss is equal to dataQuery.daily_summary_recipients minus the deleted item and push the new one
                      const newRecipients = dataQuery.dailySummaryRecipients
                        ? [
                            ...dataQuery.dailySummaryRecipients.filter((_item: unknown, i: number) => i !== index),
                            newSummaryRecipient,
                          ]
                        : [newSummaryRecipient];
                      const newVariables: SaveAccountV2MutationVariables = {
                        urlName: dataQuery.urlName,
                        setSummaryRecipients: newRecipients,
                      };
                      const sortedArray = [...newRecipients].sort();
                      saveRecipients({
                        variables: newVariables,
                        update: (cache, { data }) => {
                          if (data?.saveAccountV2.__typename !== "ResultOk") return;
                          cache.modify({
                            id: cache.identify({
                              __typename: "Query",
                              urlName: dataQuery.urlName,
                            }),
                            fields: {
                              account(oldData) {
                                return {
                                  ...oldData,
                                  dailySummaryRecipients: sortedArray,
                                };
                              },
                            },
                          });
                        },
                      });
                    }}
                    handleBlur={() => {
                      resetField(`recipients.${index}`);
                    }}
                  />
                </Stack>
              ))}
              {showNewRecip ? (
                <Stack sx={{ width: "300px", height: "60px", flexDirection: "row", mt: 2 }}>
                  <EditableField
                    key="email"
                    control={control}
                    name="email"
                    rules={{ validate: validateEmail }}
                    helperText={formState.errors?.email?.message ? formState.errors.email.message : ""}
                    label=""
                    error={Boolean(formState.errors?.email?.message)}
                    permission={Permissions.ManageAccountSettings}
                    onEscape={() => {
                      resetField("email");
                      setShowNewRecip(false);
                    }}
                    onEnter={() => {
                      const newSummaryRecipient = getValues("email");
                      // newRecipients is equal to the dailySummaryRecipients array and the push of addSummaryRecipient
                      const newRecipients = dataQuery.dailySummaryRecipients
                        ? [...dataQuery.dailySummaryRecipients, newSummaryRecipient]
                        : [newSummaryRecipient];
                      const newVariables: SaveAccountV2MutationVariables = {
                        urlName: url_name ?? "",
                        setSummaryRecipients: newRecipients,
                      };
                      const sortedArray = [...newRecipients].sort();
                      saveRecipients({
                        variables: newVariables,
                        update: (cache, { data }) => {
                          if (data?.saveAccountV2.__typename !== "ResultOk") return;
                          cache.modify({
                            id: cache.identify({
                              __typename: "Query",
                              urlName: dataQuery.urlName,
                            }),
                            fields: {
                              account(oldData) {
                                return {
                                  ...oldData,
                                  dailySummaryRecipients: sortedArray,
                                };
                              },
                            },
                          });
                        },
                      });
                      setShowNewRecip(false);
                    }}
                    handleBlur={() => {
                      setShowNewRecip(false);
                    }}
                    dataCy="new-recipient-field"
                    focusable={true}
                  />
                </Stack>
              ) : null}
              {showNewRecip ? null : (
                // this is a hack to keep a new column from appearing when the add button is clicked
                <IfPermission permission={Permissions.ManageAccountSettings}>
                  <Stack sx={{ height: "60px", mt: 2 }}></Stack>
                </IfPermission>
              )}
            </ListContainer>
          </Grid>
        </Grid>
      </BaseAdminContainer>
    </form>
  );
};

export { SettingsTab };
